/**
 * Alerts — threshold rules over semantic metrics ("ROAS < 2.0 weekly").
 * In-app/email delivery via the notification framework; optional Slack
 * webhook per rule (write-only credential).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Modal,
  Page,
  Select,
  SkeletonBodyText,
  SkeletonPage,
  Text,
  TextField,
} from '@shopify/polaris';

import {
  createAlertRule,
  deleteAlertRule,
  listAlertRules,
  listAskMetrics,
  testAlertRule,
  updateAlertRule,
} from '../services/marketingSuiteApi';
import type { AlertRule, MetricInfo } from '../types/marketingSuite';

const COMPARATORS = [
  { label: 'falls below (<)', value: 'lt' },
  { label: 'is at or below (≤)', value: 'lte' },
  { label: 'rises above (>)', value: 'gt' },
  { label: 'is at or above (≥)', value: 'gte' },
];

const COMPARATOR_LABELS: Record<string, string> = {
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

export default function Alerts() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [metrics, setMetrics] = useState<MetricInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [metricKey, setMetricKey] = useState('net_roas');
  const [comparator, setComparator] = useState('lt');
  const [threshold, setThreshold] = useState('2');
  const [channel, setChannel] = useState('in_app');
  const [webhook, setWebhook] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const [ruleList, metricList] = await Promise.all([
        listAlertRules(),
        listAskMetrics(),
      ]);
      setRules(ruleList);
      setMetrics(metricList.metrics);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const thresholdValue = Number(threshold);
  const unitOfMetric = (key: string) =>
    metrics.find((m) => m.key === key)?.unit ?? '';
  // A currency-valued threshold is meaningless across ledgers (100 USD
  // vs 100 JPY) — the server rejects monetary rules without an explicit
  // currency, so the form requires one too (PR #11 review).
  const isCurrencyMetric = unitOfMetric(metricKey) === 'currency';
  const currencyOk = /^[A-Za-z]{3}$/.test(currencyFilter.trim());
  const canCreate =
    threshold.trim() !== '' &&
    Number.isFinite(thresholdValue) &&
    (!isCurrencyMetric || currencyOk) &&
    (channel !== 'slack' || webhook.trim() !== '');

  // Percent metrics are FRACTIONS in the semantic layer (0.2 = 20%). The
  // form speaks percent because humans do — a literal "20" would have
  // created a threshold of 20 (2000%) that fires on everything or never
  // (PR #11 review). Conversion happens once, at the submit boundary;
  // stored values convert back for display.
  const unitOf = unitOfMetric;
  const isPercentMetric = unitOf(metricKey) === 'percent';
  const displayValue = (key: string, v: number) =>
    unitOf(key) === 'percent' ? `${(v * 100).toFixed(2)}%` : String(v);

  const create = async () => {
    if (!canCreate) {
      setError(
        isCurrencyMetric && !currencyOk
          ? 'Currency-valued metrics need a 3-letter currency code (e.g. USD).'
          : channel === 'slack' && !webhook.trim()
            ? 'A Slack webhook URL is required for the Slack channel.'
            : 'Enter a numeric threshold.'
      );
      return;
    }
    try {
      await createAlertRule({
        name:
          name ||
          `${metrics.find((m) => m.key === metricKey)?.label ?? metricKey} ${comparator} ${threshold}${isPercentMetric ? '%' : ''}`,
        metric_key: metricKey,
        comparator,
        threshold: isPercentMetric ? thresholdValue / 100 : thresholdValue,
        channel,
        ...(isCurrencyMetric
          ? { filters: { currency: currencyFilter.trim().toUpperCase() } }
          : {}),
        ...(channel === 'slack' && webhook
          ? { slack_webhook_url: webhook }
          : {}),
      });
      setShowCreate(false);
      setName('');
      setWebhook('');
      setCurrencyFilter('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  };

  const toggle = async (rule: AlertRule) => {
    try {
      await updateAlertRule(rule.id, {
        name: rule.name,
        metric_key: rule.metric_key,
        comparator: rule.comparator,
        threshold: rule.threshold,
        period_type: rule.period_type,
        filters: rule.filters,
        channel: rule.channel,
        cooldown_minutes: rule.cooldown_minutes,
        enabled: !rule.enabled,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const test = async (rule: AlertRule) => {
    try {
      const result = await testAlertRule(rule.id);
      if (!result.evaluated) {
        const reasonText =
          result.reason === 'no_closed_period'
            ? 'no closed period to evaluate yet'
            : (result.reason ?? 'no data yet');
        setNotice(`“${rule.name}”: ${reasonText}.`);
        return;
      }
      const valueText =
        result.value != null
          ? displayValue(rule.metric_key, result.value)
          : String(result.value);
      // The hourly sweep SKIPS rules whose upstream source is stale or
      // whose newest closed bucket has not materialized — a preview
      // claiming WOULD FIRE in those states disagrees with actual
      // delivery exactly when it matters (PR #11 review).
      const skipReason = result.stale_source
        ? `its ${result.stale_source.source} data is stale (${result.stale_source.status})`
        : result.latest_period_current === false
          ? 'the newest closed bucket has not materialized'
          : null;
      if (skipReason) {
        setNotice(
          `“${rule.name}” evaluates to ${valueText} (${result.breached ? 'crosses' : 'within'} the threshold), but the hourly sweep would SKIP it — ${skipReason}.`
        );
        return;
      }
      setNotice(
        `“${rule.name}” evaluates to ${valueText} — ${result.breached ? 'WOULD FIRE' : 'ok, no breach'}.`
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    }
  };

  if (loading) {
    return (
      <SkeletonPage title="Alerts">
        <Card>
          <SkeletonBodyText lines={6} />
        </Card>
      </SkeletonPage>
    );
  }

  const rows = rules.map((rule) => [
    rule.name,
    `${rule.metric_label ?? rule.metric_key} ${
      COMPARATOR_LABELS[rule.comparator] ?? rule.comparator
    } ${displayValue(rule.metric_key, rule.threshold)} (${rule.period_type})`,
    rule.channel === 'slack' ? 'Slack + in-app' : 'In-app + email',
    rule.last_observed_value != null
      ? displayValue(rule.metric_key, rule.last_observed_value)
      : '—',
    rule.last_triggered_at
      ? new Date(rule.last_triggered_at).toLocaleString()
      : 'never',
    (
      <InlineStack gap="100" key={rule.id}>
        <Button size="micro" onClick={() => void test(rule)}>
          Test
        </Button>
        <Button size="micro" onClick={() => void toggle(rule)}>
          {rule.enabled ? 'Pause' : 'Resume'}
        </Button>
        <Button
          size="micro"
          tone="critical"
          onClick={() => {
            if (window.confirm(`Delete alert “${rule.name}”?`)) {
              void deleteAlertRule(rule.id)
                .then(load)
                .catch((e) =>
                  setError(
                    e instanceof Error ? e.message : 'Delete failed'
                  )
                );
            }
          }}
        >
          Delete
        </Button>
      </InlineStack>
    ) as unknown as string,
  ]);

  return (
    <Page
      title="Alerts"
      subtitle="Threshold rules evaluated on every sync tick. Breaches notify in-app/email — and Slack if configured."
      primaryAction={{
        content: 'New alert',
        onAction: () => setShowCreate(true),
      }}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {notice && (
          <Banner tone="info" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}
        <Card>
          {rules.length ? (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
              headings={[
                'Name',
                'Condition',
                'Delivery',
                'Last value',
                'Last fired',
                '',
              ]}
              rows={rows}
            />
          ) : (
            <BlockStack gap="200">
              <Text as="p" tone="subdued">
                No alert rules yet. Try “Net ROAS falls below 2.0, weekly.”
              </Text>
            </BlockStack>
          )}
        </Card>
      </BlockStack>

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New alert rule"
        primaryAction={{
          content: 'Create',
          disabled: !canCreate,
          onAction: () => void create(),
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Select
              label="Metric"
              options={metrics.map((m) => ({ label: m.label, value: m.key }))}
              value={metricKey}
              onChange={setMetricKey}
            />
            <Select
              label="Condition"
              options={COMPARATORS}
              value={comparator}
              onChange={setComparator}
            />
            <TextField
              label="Threshold"
              type="number"
              value={threshold}
              onChange={setThreshold}
              autoComplete="off"
              suffix={isPercentMetric ? '%' : undefined}
              helpText={
                isPercentMetric
                  ? 'Enter a percentage: 20 means 20% (stored as the fraction 0.2).'
                  : undefined
              }
            />
            {isCurrencyMetric && (
              <TextField
                label="Currency"
                value={currencyFilter}
                onChange={setCurrencyFilter}
                autoComplete="off"
                placeholder="USD"
                maxLength={3}
                helpText="Monetary thresholds apply to one ledger — a single number can't compare USD and JPY."
              />
            )}
            <Select
              label="Delivery"
              options={[
                { label: 'In-app + email', value: 'in_app' },
                { label: 'Slack (+ in-app)', value: 'slack' },
              ]}
              value={channel}
              onChange={setChannel}
            />
            {channel === 'slack' && (
              <TextField
                label="Slack webhook URL"
                value={webhook}
                onChange={setWebhook}
                autoComplete="off"
                helpText="Stored encrypted; never shown again."
              />
            )}
            <TextField
              label="Name (optional)"
              value={name}
              onChange={setName}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
